import { z } from 'zod';

export type Dimension = {
  name?: string;
  label?: string;
  defaultName?: string;
  order?: number;
  isDisabled?: boolean;
  isReadOnly?: boolean;
};

const CurrencySchema = z.object({
  value: z.string(),
  displayName: z.string(),
  sign: z.string(),
});

const GoalSchema = z.object({
  id: z.number(),
  metric: z.coerce.number(),
  metricLabel: z.string(),
  isPrimary: z.boolean(),
  value: z.number(),
  goalValue: z.coerce.number(),
  valueType: z.string(),
  justAdded: z.optional(z.boolean()),
  objective: z.optional(z.enum(['awareness', 'all', 'performance'])),
  isNew: z.optional(z.boolean()),
});

const TileSchema = z.object({
  id: z.string().or(z.number()),
});

const CampaignsSchema = z.object({
  displayCampaign: z.array(TileSchema).optional(),
  ghostCampaign: z.array(TileSchema).optional(),
  searchCampaign: z.array(TileSchema).optional(),
  socialCampaign: z.array(TileSchema).optional(),
  videoCampaign: z.array(TileSchema).optional(),
  emailCampaign: z.array(TileSchema).optional(),
});

const MediaSchema = z.object({
  id: z.number(),
  name: z.string(),
  file: z.string(),
  uploadedAt: z.string(),
  type: z.enum(['Presentation', 'Video', 'Banner']),
});

export const CampaignSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  adServer: z.string(),
  published: z.boolean(),
  description: z.string(),
  currency: CurrencySchema,
  budget: z.string().nullable(),
  allocatedBudget: z.string().nullable(),
  dateFrom: z.string().or(z.null()),
  dateTo: z.string().or(z.null()),
  budgetSpent: z.string().nullable(),
  status: z.string(),
  goals: z.array(GoalSchema),
  campaigns: CampaignsSchema,
  media: z.array(MediaSchema).optional(),
  isCarbonNeutralAllowed: z.boolean(),
});

export type CampaignType = z.infer<typeof CampaignSchema>;

/**
 * @deprecated Use `CampaignType` instead.
 */
export type Campaign = {
  id: number | string;
  name: string;
  published: boolean;
  description: string;
  currency: Currency;
  budget: string;
  allocatedBudget: number;
  dateFrom: string | null;
  dateTo: string | null;
  budgetSpent: string;
  status: string;
  objective: string;
  goals: Array<Goal>;
  campaigns: Campaigns;
  media: Media[];
};

export type Campaigns = {
  emailCampaign: Array<Tile>;
  displayCampaign: Array<Tile>;
  searchCampaign: Array<Tile>;
  videoCampaign: Array<Tile>;
  socialCampaign: Array<Tile>;
};

export type Tile = {
  id: number | string;
  name: string;
  completed: boolean;
  providers: Array<string>;
  platformMedia: string;
  budget: string;
  dateFrom: string | null;
  dateTo: string | null;
  budgetSpent: string;
  status: string;
  objective: string;
  goals: Array<Goal>;
  isGhost?: boolean;
  currency?: Currency;
  detail?: {
    typeOfBuying: string | null;
    // Don't use this field.
    name: string;
    type: string;
    integration?: {
      provider: string;
      id: number;
    };
  };
  lastApprovedMediaPlanItem?: {
    fields: {
      budget: string;
      dateFrom: string;
      dateTo: string;
    };
  };
};

export type Currency = {
  value: string;
  displayName: string;
  sign: string;
};

export enum Objective {
  awareness = 'awareness',
  all = 'all',
  performance = 'performance',
}

export type Goal = {
  id: number;
  metric: string;
  metricLabel: string;
  isPrimary: boolean;
  value: number;
  goalValue: number;
  valueType: string;
  justAdded?: boolean;
  objective: Objective;
  isNew?: boolean;
};

export type Media = {
  id: number;
  name: string;
  file: any;
  uploadedAt: string;
  type: 'Presentation' | 'Video' | 'Banner';
};
